import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { CartItemDetails } from '@customTypes/cart-items';
import { CheckoutInfo } from '@customTypes/checkout-info';
import { useCookies } from 'react-cookie';
import User from '@customTypes/user';
import { getDetailedDate, isToday } from '@helpers/date';
import { UserAction } from '@customTypes/context';
import actionTypes from '@context/actionTypes';
import { selectADateClickOnFixedBar, bookNowScrollToProductsFixedBar } from '@events/DLP';
import HotelDetails from '@customTypes/hotel-details';
import useCheckout from '@hooks/useCheckout';
import useHotSpot from '@hooks/useHotSpot';
import Button from '@solar/common/Button/ButtonV2';

import { useFeatureFlagContext } from '@context/FeatureFlagContext';
import { isTreatment } from '@helpers/experiment';
import { cn } from '@helpers/className';
import { FreeCancellationText } from '@components/FreeCancellation';
import Currency from '../Currrency/Currency';

type Props = {
  localDate: Date | undefined;
  onBookNowClick: () => void;
  cartData: CartItemDetails | undefined;
  hotelId: number | undefined;
  hotelDetails: HotelDetails;
  cartId: string;
  setIsYourCartExpanded: Dispatch<SetStateAction<boolean>>;
  checkoutDetails: CheckoutInfo | undefined;
  dispatch: React.Dispatch<UserAction>;
  showFairPricing: boolean;
};

export default function FixedBottomBanner({
  localDate,
  cartData,
  hotelId,
  hotelDetails,
  cartId,
  setIsYourCartExpanded,
  onBookNowClick,
  checkoutDetails,
  dispatch,
  showFairPricing,
}: Props) {
  const [cartHotelId, setCartHotelId] = useState<number>(0);
  const [cookies] = useCookies();
  const { navigateToCheckoutConfirmation } = useCheckout();
  const user: User = cookies.userInformation;
  const { getSubtotal } = useHotSpot();

  const { freeCancellationExperiment } = useFeatureFlagContext();

  const openSmartCalendar = () => {
    // Event
    selectADateClickOnFixedBar(hotelDetails);

    dispatch({
      type: actionTypes.OPEN_DATE_PICKER,
      payload: true,
    });
  };

  const handleGoToCheckout = () => {
    if (localDate && isToday(localDate)) {
      dispatch({
        type: actionTypes.OPEN_SAME_DAY_BOOKING_WARNING,
        payload: true,
      });
    } else {
      navigateToCheckoutConfirmation();
    }
  };

  const handleChangeDateClick = () => {
    // Event
    bookNowScrollToProductsFixedBar(hotelDetails);

    onBookNowClick();
  };

  useEffect(() => {
    (async () => {
      if (
        cartData &&
        cartData?.items.length > 0 &&
        cartData?.items[0] &&
        cartData?.items[0].hotel_id
      ) {
        setCartHotelId(cartData?.items[0].hotel_id);
      }
    })();
  }, [cartData, cartId, user]);

  const shouldShowCancellationMessage = isTreatment(freeCancellationExperiment);
  const renderFilledCartFooter = () => (
    <>
      <div className="flex flex-col items-start mt-4 px-5">
        <p className="font-rp-pn-regular text-15">Subtotal:</p>
        <button
          type="button"
          className="text-sm font-rp-pn-semi-bold"
          onClick={() => setIsYourCartExpanded(true)}
        >
          <Currency
            value={checkoutDetails ? getSubtotal(checkoutDetails, showFairPricing) : '0'}
            valueClasses="text-17 border-b-2 border-black"
            isoCodeClasses="text-15"
            precision={0}
          />
        </button>
        {shouldShowCancellationMessage && <FreeCancellationText />}
      </div>

      <div className="pb-4 mt-4 pr-5">
        <div className="w-108">
          <Button size="lg" onClick={handleGoToCheckout} variant="primary">
            Reserve
          </Button>
        </div>
      </div>
    </>
  );

  const renderEmptyCartFooter = () => {
    const dateLabel = shouldShowCancellationMessage ? '' : 'Date';
    return (
      <>
        <div className="flex flex-col items-start mt-4 px-5">
          <p className="font-rp-pn-regular text-15">{dateLabel}</p>
          {localDate && (
            <>
              <button
                type="button"
                className="text-15 font-rp-pn-semi-bold d:font-semibold"
                onClick={openSmartCalendar}
              >
                <span
                  className={cn(
                    !shouldShowCancellationMessage && 'border-b border-rp-primary-black',
                  )}
                >
                  {getDetailedDate(new Date(localDate))}
                </span>
              </button>
              {shouldShowCancellationMessage && <FreeCancellationText />}
            </>
          )}
        </div>

        <div className="pb-4 mt-4 pr-5">
          <div className="w-108">
            <Button size="lg" onClick={handleChangeDateClick} variant="primary">
              Reserve
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="flex justify-between fixed-dlp-banner d:hidden">
      {cartData && cartData?.items.length > 0 && cartHotelId === hotelId
        ? renderFilledCartFooter()
        : renderEmptyCartFooter()}
    </div>
  );
}
